import { View, VStack, HStack, Pressable, Divider } from "@gluestack-ui/themed";
import { Text } from "@repo/core/components/core/Text";
import { Button } from "@repo/core/components/core/Button";
import Scaffolds from "@repo/core/components/layout/Scaffolds";
import { StatusBarVariantEnum } from "@repo/core/components/layout/StatusBar/types";
import { NewTransactionReviewController } from "./controller";
import { Icon } from "@repo/core/components/core/Icon";
import { numberToBrl } from "@repo/core/utils/currency.utils";
import { AlertModal } from "@repo/core/components/layout/ModalTemplate/AlertModal";
import { formatDateTimeString } from "@repo/core/utils/date.utils";
import { AlertModalVariant } from "@repo/core/components/layout/ModalTemplate/AlertModal/types";

const NewTransactionReview = () => {
  const {
    isInstallmentsPayment,
    transactionData,
    handleFinishTransaction,
    handleFidelityPress,
    handleValuePress,
    handleBack,
    isMobile,
    title,
    alertModalRef,
    alertOptions,
    selectedStore
  } = NewTransactionReviewController();

  return (
    <Scaffolds.Root
      showHeader={false}
      statusBarVariant={StatusBarVariantEnum.STORE_SELECT}
    >
      <Scaffolds.StoreSelect onBackPress={handleBack} title={title}>
        <Scaffolds.Form
          showHeader={false}
          footerContent={
            <View w="$full" gap="$4" flexDirection="column" px="$4" pb="$4">
              <Button
                size="xl"
                action="primary"
                text="Concluir transação"
                onPress={handleFinishTransaction}
              />
            </View>
          }
        >
          <VStack flex={1} gap="$6">
            <VStack gap="$6" px="$4">
              <VStack gap="$4">
                <HStack gap="$1">
                  <Icon
                    name="alert-circle"
                    size={16}
                    color="$warning700"
                    variant="regular"
                  />
                  <Text color="$textLight700" fontSize="$xs">
                    Verifique se a loja selecionada acima está correta.
                  </Text>
                </HStack>
                <Text fontWeight={500} color="$textLight800">
                  Revise os detalhes da transação
                </Text>
              </VStack>

              <VStack gap="$4">
                <VStack gap="$1">
                  <Text color="$textLight700" fontSize="$2xs">
                    Data
                  </Text>
                  <Text color="$textLight900" fontWeight={500}>
                    {transactionData.date}
                  </Text>
                </VStack>

                <VStack gap="$1">
                  <Text color="$textLight700" fontSize="$2xs">
                    Forma de Pagamento
                  </Text>
                  <Text color="$textLight900" fontWeight={500}>
                    {transactionData.paymentMethod}
                  </Text>
                </VStack>

                {transactionData.installments && (
                  <VStack gap="$1">
                    <Text color="$textLight700" fontSize="$2xs">
                      Parcelamento
                    </Text>
                    <Text color="$textLight900" fontWeight={500}>
                      {transactionData.installments}x
                    </Text>
                  </VStack>
                )}

                <Pressable onPress={handleFidelityPress}>
                  <View bg="$primary0" p="$3" py="$4" borderRadius="$lg">
                    <HStack justifyContent="space-between" alignItems="center">
                      <VStack>
                        <Text
                          color="$primary700"
                          fontWeight={600}
                          lineHeight={20}
                        >
                          Programa de Fidelidade
                        </Text>
                        <Text fontSize="$2xs" lineHeight={16}>
                          Saldo total:{" "}
                          {numberToBrl(transactionData.availableBonus || 0)}
                        </Text>
                      </VStack>
                      <HStack gap="$2">
                        <Text color="$textLight900" fontWeight={500}>
                          {numberToBrl(transactionData.bonus || 0)}
                        </Text>
                        <Icon
                          name="chevron-right"
                          size={20}
                          color="$primary700"
                        />
                      </HStack>
                    </HStack>
                  </View>
                </Pressable>
              </VStack>
            </VStack>
            <Divider />

            <VStack flex={1} gap="$4" px="$4">
              <Text fontSize="$lg" fontWeight={500}>
                Resumo de valores
              </Text>

              <VStack gap="$3">
                <HStack justifyContent="space-between">
                  <Text color="$textLight700" fontSize="$sm" fontWeight={500}>
                    Subtotal
                  </Text>
                  <Text color="$textLight900">
                    {isInstallmentsPayment
                      ? numberToBrl(transactionData.installmentTotal)
                      : numberToBrl(transactionData.subtotal)}
                  </Text>
                </HStack>

                <HStack justifyContent="space-between">
                  <Text color="$textLight700" fontSize="$sm" fontWeight={500}>
                    Bônus
                  </Text>
                  <Text color="$primary700">
                    -{numberToBrl(transactionData.bonus)}
                  </Text>
                </HStack>

                <HStack justifyContent="space-between">
                  <Text color="$textLight700" fontSize="$sm" fontWeight={600}>
                    Valor total
                  </Text>
                  <Text color="$textLight900" fontWeight={600}>
                    {isInstallmentsPayment
                      ? numberToBrl(
                          transactionData.installmentTotal -
                            transactionData.bonus
                        )
                      : numberToBrl(transactionData.total)}
                  </Text>
                </HStack>
              </VStack>
            </VStack>
            <VStack flex={1} justifyContent="flex-end" gap="$4">
              {isMobile && <Divider h="$2" bg="$backgroundDark100" />}
              <HStack
                py={isMobile ? "$0" : "$3"}
                bg={isMobile ? "$backgroundLight0" : "$backgroundLight100"}
                borderRadius={isMobile ? "$none" : "$lg"}
                justifyContent="space-between"
                px="$6"
              >
                <VStack gap="$1">
                  <Text fontWeight={600} fontSize="$xl" color="$textLight900">
                    {isInstallmentsPayment
                      ? `${transactionData.installments}x de ${numberToBrl(
                          transactionData.installmentValue
                        )}`
                      : numberToBrl(transactionData.subtotal)}
                  </Text>
                  <Text>
                    Valor total de{" "}
                    {isInstallmentsPayment
                      ? numberToBrl(transactionData.installmentTotal)
                      : numberToBrl(transactionData.total)}
                  </Text>
                </VStack>
                <VStack gap="$1" alignItems="flex-end" justifyContent="center">
                  <Pressable onPress={handleValuePress}>
                    <Icon name="pen-square" size={20} color="$secondary800" />
                  </Pressable>
                </VStack>
              </HStack>
            </VStack>
          </VStack>
        </Scaffolds.Form>
      </Scaffolds.StoreSelect>
      <AlertModal
        ref={alertModalRef}
        title="Transação realizada"
        variant={AlertModalVariant.success}
        options={alertOptions}
      >
        <VStack alignItems="center" justifyContent="center" gap="$2">
          <Text fontWeight={600} textAlign="center">
            {selectedStore?.descrLoja}
          </Text>
          <Text fontWeight={600} textAlign="center">
            {numberToBrl(transactionData.subtotal)}
          </Text>
          <Text fontSize="$sm" textAlign="center">
            {formatDateTimeString(new Date())}
          </Text>
        </VStack>
      </AlertModal>
    </Scaffolds.Root>
  );
};

export default NewTransactionReview;
